import {useEffect, useRef, useState} from 'react';
import { Field, Textarea, Label } from '@headlessui/react'
import clsx from 'clsx'
import { useTools } from "../../../../ToolProvider.jsx";
import {useEditorTypeRightContext, useEditorTypeRightToggleContext} from "../../../../SelectedTypeProvider.jsx";
import { DEFAULT_EDITOR_FONT, EDITOR_FONTS, ensureFontsLoaded } from "../../../../../../fabricjs/fonts.js";
import { showConfirm, showErrorAlert } from "../../../../../../components/HelperAlerts/HelpersAlerts.jsx";

// La lista de fuentes vive en fabricjs/fonts.js: es la misma que usa el canvas
// y tenerla dos veces ya nos dejó "Rock Salt" sin renderizar.
const fuentes = EDITOR_FONTS

const DEFAULT_FONT = DEFAULT_EDITOR_FONT;

// Motivo del fallo de vectorizar -> mensaje para el usuario (ver
// vectorizeTextClient/onVectorizeTextServer en editor.js/ToolProvider.jsx).
const VECTORIZE_ERROR_MESSAGES = {
    'no-text': 'No hay ningun texto seleccionado.',
    'empty': 'El texto esta vacio.',
    'font-unavailable': 'Esta fuente no se puede vectorizar.',
    'server-error': 'No se pudo vectorizar en el servidor. Intentalo de nuevo.',
    'no-editor': 'El editor no esta listo todavia.',
};

const TextEditor = () => {
    const editorTypeRight = useEditorTypeRightContext()
    const setEditorTypeRight = useEditorTypeRightToggleContext()
    const {
        onSetFontText,
        onSetTextStyle,
        onPreviewText,
        onRestoreTextPreview,
        onAddText,
        onUpdateText,
        onVectorizeTextClient,
        onVectorizeTextServer,
    } = useTools()
    const [feedback, setFeedback] = useState("")
    const [isVectorizing, setIsVectorizing] = useState(false)

    // Las tarjetas son las muestras de cada fuente: se piden todas al abrir el
    // panel para que ninguna se vea con la de reserva.
    useEffect(() => {
        ensureFontsLoaded(Object.keys(fuentes))
    }, [])

    const currentData = editorTypeRight.data ?? {}
    const isNewText = currentData.is_new === true
    const text = currentData.text ?? ""
    const selectedFont = currentData.font || DEFAULT_FONT
    const selectedFontWeight = currentData.fontWeight || 'normal'
    const selectedFontStyle = currentData.fontStyle || 'normal'
    const isBold = selectedFontWeight === 'bold'
    const isItalic = selectedFontStyle === 'italic'
    const originalTextRef = useRef(null)
    const hasAppliedRef = useRef(false)

    const updateEditorData = (data) => {
        setEditorTypeRight(prev => ({
            ...prev,
            type: "editor_text",
            data: {
                ...prev.data,
                ...data,
            }
        }))
    }

    const handleTextChange = (event) => {
        if (!isNewText) hasAppliedRef.current = false
        updateEditorData({ text: event.target.value })
        if (feedback) setFeedback("")
    }

    const handleFontSelect = (font) => {
        if (!isNewText) hasAppliedRef.current = false
        updateEditorData({ font })
        if (feedback) setFeedback("")
    }

    const handleStyleToggle = (styleKey) => {
        const nextStyle = {
            fontWeight: selectedFontWeight,
            fontStyle: selectedFontStyle,
        }

        if (styleKey === 'fontWeight') {
            nextStyle.fontWeight = isBold ? 'normal' : 'bold'
        } else if (styleKey === 'fontStyle') {
            nextStyle.fontStyle = isItalic ? 'normal' : 'italic'
        }

        updateEditorData(nextStyle)
        if (!isNewText) hasAppliedRef.current = false
        if (feedback) setFeedback("")
    }

    const applyText = (
        fontOverride = selectedFont,
        styleOverride = { fontWeight: selectedFontWeight, fontStyle: selectedFontStyle },
    ) => {
        if (!text.trim()) {
            setFeedback("Escribe un texto antes de continuar.")
            return
        }

        if (isNewText) {
            onAddText(text, fontOverride, styleOverride)
            updateEditorData({ is_new: false, text, font: fontOverride, ...styleOverride })
            setFeedback("Texto anadido.")
        }
        else {
            onUpdateText(text)
            onSetFontText(fontOverride)
            onSetTextStyle(styleOverride)
            hasAppliedRef.current = true
            originalTextRef.current = {
                text,
                fontFamily: fontOverride,
                fontStyle: styleOverride.fontStyle,
                fontWeight: styleOverride.fontWeight,
            }
            updateEditorData({ is_new: false, text, font: fontOverride, ...styleOverride })
            setFeedback("Texto aplicado.")
        }
    }

    useEffect(() => {
        if (isNewText) {
            originalTextRef.current = null
            hasAppliedRef.current = false
            return undefined
        }

        if (!originalTextRef.current) {
            originalTextRef.current = {
                text,
                fontFamily: selectedFont,
                fontStyle: selectedFontStyle,
                fontWeight: selectedFontWeight,
            }
            hasAppliedRef.current = false
        }

        return () => {
            if (!hasAppliedRef.current && originalTextRef.current) {
                onRestoreTextPreview?.(originalTextRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (isNewText) return
        onPreviewText?.({
            text,
            fontFamily: selectedFont,
            fontStyle: selectedFontStyle,
            fontWeight: selectedFontWeight,
        })
    }, [isNewText, selectedFont, selectedFontStyle, selectedFontWeight, text])

    const handleSubmit = (event) => {
        event.preventDefault()
        applyText()
    }

    // El textarea deja Enter como salto de linea (el texto puede ocupar
    // varias lineas en el lienzo); Ctrl/Cmd+Enter manda sin soltar el teclado.
    const handleTextareaKeyDown = (event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault()
            applyText()
        }
    }

    // Dos motores en paralelo para comparar (ver editor.js): opentype.js en
    // el cliente vs Inkscape en el servidor. Es un montaje temporal — una
    // vez decidido cuál se queda, se quita el otro botón.
    const handleVectorize = async (runVectorize) => {
        const confirmed = await showConfirm({
            title: "Vectorizar texto",
            text: "Una vez vectorizado no podras modificar el texto ni la tipografia. ¿Continuar?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Vectorizar",
            cancelButtonText: "Cancelar",
        })
        if (!confirmed.isConfirmed) return

        setIsVectorizing(true)
        try {
            const result = await runVectorize()
            if (result?.ok) {
                setEditorTypeRight({ type: null, data: null })
            } else {
                showErrorAlert({
                    text: VECTORIZE_ERROR_MESSAGES[result?.reason] || "No se pudo vectorizar el texto.",
                })
            }
        } finally {
            setIsVectorizing(false)
        }
    }

    return (
        <form
            className="text-editor-panel"
            data-customiz3r-text-editor="true"
            onSubmit={handleSubmit}
        >
            <Field className="text-editor-controls">
                <Label className="text-editor-label">
                    {isNewText ? "Texto nuevo" : "Texto seleccionado"}
                </Label>
                <Textarea
                    placeholder="Escribe el texto"
                    className={clsx('text-editor-input')}
                    data-customiz3r-text-editor="true"
                    onChange={handleTextChange}
                    onKeyDown={handleTextareaKeyDown}
                    value={text}
                    rows={2}
                    autoComplete="off"
                />
                <div className="text-editor-action-row">
                    <div className="text-editor-style-buttons" aria-label="Estilo del texto">
                        <button
                            type="button"
                            className={`text-editor-style-button ${isBold ? 'text-editor-style-button--active' : ''}`}
                            aria-pressed={isBold}
                            title="Negrita"
                            onClick={() => handleStyleToggle('fontWeight')}
                        >
                            B
                        </button>
                        <button
                            type="button"
                            className={`text-editor-style-button text-editor-style-button--italic ${isItalic ? 'text-editor-style-button--active' : ''}`}
                            aria-pressed={isItalic}
                            title="Cursiva"
                            onClick={() => handleStyleToggle('fontStyle')}
                        >
                            I
                        </button>
                    </div>
                    <button type="submit" className="text-editor-apply-button">
                        {isNewText ? "Anadir texto" : "Aplicar"}
                    </button>
                    {feedback && (
                        <span className="text-editor-feedback" role="status">
                            {feedback}
                        </span>
                    )}
                </div>
            </Field>

            {!isNewText && (
                <div className="text-editor-action-row text-editor-vectorize-row">
                    <button
                        type="button"
                        className="text-editor-apply-button"
                        disabled={isVectorizing}
                        onClick={() => handleVectorize(onVectorizeTextClient)}
                    >
                        Vectorizar (rapido)
                    </button>
                    <button
                        type="button"
                        className="text-editor-apply-button"
                        disabled={isVectorizing}
                        onClick={() => handleVectorize(onVectorizeTextServer)}
                    >
                        Vectorizar (Inkscape)
                    </button>
                </div>
            )}

            <div className="text-editor-font-list">
                {Object.keys(fuentes).map((fontName, index) => (
                <button
                    type="button"
                    key={index}
                    className={`font-card text-editor-font-card ${selectedFont === fontName ? 'font-card--selected' : ''}`}
                    data-customiz3r-text-editor="true"
                    onClick={() => handleFontSelect(fontName)}
                    onDoubleClick={() => applyText(fontName)}
                >
                    <span
                        className={`text-editor-font-preview font-${fuentes[fontName]}`}
                        data-bold={isBold ? 'true' : 'false'}
                        data-italic={isItalic ? 'true' : 'false'}
                        style={{
                            fontFamily: fontName,
                            fontStyle: selectedFontStyle,
                            fontWeight: selectedFontWeight,
                        }}
                    >
                        {text !== '' ? text : 'C3D'}
                    </span>
                    <span className="text-editor-font-name">{fontName}</span>
                </button>
                ))}
            </div>
        </form>
    );
};

export default TextEditor;
